import React, { useState, useEffect } from 'react';

// bootstrap
import { Form, Button, Alert } from 'react-bootstrap'

// custom components 
import { CustomSpinner } from '_atoms';
import { AssistanceSelector, CustomModal } from "_molecules";
import BookingNotesSuggestions from './booking-notes-suggestions';

// styles 
import { Colors } from '_styles';

// redux
import { useSelector, useDispatch } from 'react-redux';
import { selectJob } from '_redux/slices/utils/menu-context';
import {
    updateTimelineBooking,
    selectUpdateStatus,
    selectUpdateError,
    resetUpdateBooking,
} from '_redux/slices/timeline/timeline-bookings';
import { selectAllTimelineAreas, selectBookingType, selectPickUpLocations } from "_redux/slices/timeline/timeline-areas";
import { resetUpdateTaskFlags, setBookingLocation, selectSetBookingLocationStatus, arrivalPaxCheckIn } from "_redux/slices/timeline/timeline-data";
import { reset as resetForm } from '_redux/slices/assistance-bookings/booking-form';
import { selectFirebaseUser, selectUserDb } from '_redux/slices/main-screen/user';
import { selectDefaultArrivalCheckInLocation, selectDefaultCheckInLocation } from '_redux/slices/timeline/timeline-control';
import { selectUsedTheme } from '_redux/slices/timeline/theme';
import { selectFlightById } from '_redux/slices/timeline/timeline-flight-list';

// date 
import moment from 'moment';

// functions 
import { eqUnorderedStringArrays, formatDayTime, getAssistanceArray, getAssistanceString } from '_utils/functions';

// drop down menu
import Select from 'react-select';

// validation
import validator from 'validator';


/**
 * V2 of the edit timeline booking form - for use with timeline-v2
 * 
 * Key difference from v1:
 * - Does NOT dispatch getBookings/getDepartureBookings to refresh timeline-data slice
 * - Instead relies on updateBooking.fulfilled being picked up by timeline-bookings slice
 *   which automatically calls updateBookingInState to update the booking in place
 */
const EditTimelineBookingFormV2 = (props) => {

    //#region redux
    const dispatch = useDispatch();

    const dbUser = useSelector(selectUserDb)
    const firebaseUser = useSelector(selectFirebaseUser);

    const job = useSelector(selectJob);                                             // selected booking to edit 
    const updateStatus = useSelector(selectUpdateStatus);                           // status of updating bookings (from timeline-bookings)
    const updateError = useSelector(selectUpdateError);
    const bookingType = useSelector(selectBookingType);                             // select bookingType (departures,arrivals,all)
    const pickUpLocations = useSelector(selectPickUpLocations);                     // pickup locations selector
    const defaultCheckInLocation = useSelector(selectDefaultCheckInLocation);       // default CheckIn Location
    const usedTheme = useSelector(selectUsedTheme);
    const setBookingLocationStatus = useSelector(selectSetBookingLocationStatus);
    const defaultArrivalCheckInLocation = useSelector(selectDefaultArrivalCheckInLocation);
    const allTimelineAreas = useSelector(selectAllTimelineAreas); // select all timelineAreas


    const [flightKey, setFlightKey] = useState(null);

    const mainFlightDocument = useSelector(selectFlightById(flightKey));

    //#endregion

    //#region local states
    const [booking, setBooking] = useState(null)                                    // selected booking
    const [firstName, setFirstName] = useState("")                                  // PAX firstName
    const [secondName, setSecondName] = useState("")                                // PAX secondName
    const [assistance, setAssistance] = useState("")                                // PAX assistance
    const [bookingNotes, setBookingNotes] = useState("")                            // Booking Notes
    const [selectedPickUp, setSelectedPickUp] = useState(null);                     // selected pick up location
    const [errorMessage, setError] = useState("");                                  // error message for displaying issues with 
    const [editAssistanceReason, setEditAssistanceReason] = useState(null);
    const [showAddReasonInput, setShowAddReasonInput] = useState(false);
    const [assistanceHistoric, setAssistanceHistoric] = useState([])
    const [originalAssistance, setOriginalAssistance] = useState([]);               // store original assistance once on load
    const [isInitialized, setIsInitialized] = useState(false);                      // track if form has been initialized
    const [selectedArrCheckInLocation, setSelectedArrCheckInLocation] = useState(null);
    //#endregion

    const [isSelfAssisted, setIsSelfAssisted] = useState(false);     // default to false (pax is not self-assisting)
    const [boardingAssistNeeded, setBoardingAssistNeeded] = useState(true);
    const [selectedBoardingPickup, setSelectedBoardingPickup] = useState(null);             // boarding pickup location when preboard not required
    const [isNoShow, setIsNoShow] = useState(false);                // no show status
    const [passengerEmail, setPassengerEmail] = useState("");       // optional, makes non-app bookings eligible for the email survey
    const [arrForLocationsTab, setArrForLocationsTab] = useState(false);
    const [showArrInLocationsTab, setShowArrInLocationsTab] = useState(false);

    const isDepartureCheckIn = props?.forCheckIn && booking?.flightConfirmationDetails?.origin == dbUser?.details?.authority;
    const trimmedEmail = isDepartureCheckIn ? passengerEmail.trim() : "";
    const emailError = trimmedEmail.length > 0 && !validator.isEmail(trimmedEmail);
    //#region useEffect

    // reset on first render 
    useEffect(() => {
        dispatch(resetUpdateBooking())
        dispatch(resetUpdateTaskFlags())
        setPickUpLocation(null);
        setArrForLocationsTab(false);
        setShowArrInLocationsTab(false);
        setSelectedBoardingPickup(null);
    }, []);

    useEffect(() => {
        if (mainFlightDocument) {
            let pickup = matchLocationByGateOrStand(pickUpLocations, mainFlightDocument?.gate, "");

            if (pickup) {
                setSelectedBoardingPickup({
                    value: pickup?._id ?? "",
                    label: pickup?.title ?? ""
                });
            }

        }
    }, [mainFlightDocument]);


    useEffect(() => {
        if (Array.isFullArray(allTimelineAreas)) {
            const holdingArea = allTimelineAreas?.filter(area => area?.holdingArea);

            if (Array.isFullArray(holdingArea)) {
                let holdingAreaLocations = [];
                holdingArea?.forEach(area => {
                    if (Array.isFullArray(area?.location)) {
                        area?.location?.forEach(loc => {
                            holdingAreaLocations.push(loc?.locationId);
                        });
                    }
                });
                if (holdingAreaLocations?.includes(selectedArrCheckInLocation?.value)) {
                    setShowArrInLocationsTab(true);
                    setArrForLocationsTab(true);
                } else {
                    setShowArrInLocationsTab(false);
                    setArrForLocationsTab(false);
                }
            } else {
                setShowArrInLocationsTab(false);
                setArrForLocationsTab(false);
            }
        }
    }, [allTimelineAreas, selectedArrCheckInLocation]);

    useEffect(() => {
        if (defaultArrivalCheckInLocation && Array.isFullArray(pickUpLocations) && booking?.flightConfirmationDetails?.destination == dbUser?.details?.authority) {
            const defaultLocation = pickUpLocations?.find(loc => loc?._id === defaultArrivalCheckInLocation);
            if (defaultLocation) {
                setSelectedArrCheckInLocation({
                    value: defaultLocation?._id ?? "",
                    label: defaultLocation?.title ?? ""
                });
            }
        }
    }, [defaultArrivalCheckInLocation, pickUpLocations, booking]);

    // get the original booking doc 
    useEffect(() => {
        if (!Object.isObjectEmpty(job)) {
            setBooking(job?.originalBookingDoc ? job?.originalBookingDoc : job)
        }
    }, [job])

    // set the passenger name and assistance by the booking - ONLY on initial load
    useEffect(() => {
        if (!Object.isObjectEmpty(booking) && !isInitialized) {
            // For ALT pickup tasks, name is in passengerInformation.title, otherwise it's in name
            const passengerName = booking.name || booking.passengerInformation?.title || "";
            const nameParts = passengerName.trim().split(/\s+/);

            setFirstName(nameParts?.[0] ?? "")
            setSecondName(nameParts?.[1] ?? "")

            // For ALT pickup tasks, assistance is in passengerInformation.assistance
            const assistanceData = booking.assistance || booking.passengerInformation?.assistance || [];
            const tempAssistance = getAssistanceArray(assistanceData)

            const hist = Array.isArray(booking?.editAssistanceHistoric)
                ? booking.editAssistanceHistoric
                : [];

            setAssistanceHistoric(hist);

            // For ALT pickup tasks, notes might be in taskNotes array
            const notesValue = booking.notes ??
                (Array.isArray(booking.taskNotes) && booking.taskNotes.length > 0
                    ? booking.taskNotes
                    : "");
            setBookingNotes(notesValue)
            setAssistance(tempAssistance)
            setOriginalAssistance(tempAssistance)  // Store original assistance for comparison

            // Initialize self-assisted state - if preboard assistance is NOT required, then pax is self-assisted
            setIsSelfAssisted(booking?.preboardAssistanceRequired === false)
            setBoardingAssistNeeded(booking?.boardingAssistNeeded === false ? false : true)

            // Initialize no show status
            setIsNoShow(booking?.noShow === true)
            setPassengerEmail(booking?.passengerEmail ?? "")

            // Initialize boarding pickup location if it exists
            if (booking?.boardingPickupLocation?.id) {
                setSelectedBoardingPickup({
                    value: booking.boardingPickupLocation.id,
                    label: booking.boardingPickupLocation.name
                });
            } else {
                setSelectedBoardingPickup(null);
                const flightDetails = booking?.flightConfirmationDetails;

                const isArrival = flightDetails?.destination === dbUser?.details?.authority;
                const flightDate = isArrival
                    ? flightDetails?.arrivalTime?.split("T")?.[0]
                    : flightDetails?.departureTime?.split("T")?.[0];
                let key = flightDetails?.carrierCode + flightDetails?.flightNumber + '_' + flightDetails?.origin + '_' + flightDetails?.destination + '_' + flightDate;
                setFlightKey(key);
            }

            setIsInitialized(true);  // Mark as initialized to prevent re-runs
        }
    }, [booking, isInitialized])

    // Handle pickup location separately - this can update when dependencies change
    useEffect(() => {
        if (!Object.isObjectEmpty(booking) && pickUpLocations?.length > 0) {
            // Only set pickup location if not already set by user
            if (!selectedPickUp) {
                if (booking.paxPositionInfo?.activity === "Checked-in" && booking.paxPositionInfo?.locationId) {
                    setPickUpLocation(booking.paxPositionInfo?.locationId);
                } else if (defaultCheckInLocation) {
                    setPickUpLocation(defaultCheckInLocation);
                }
            }
        }
    }, [booking, defaultCheckInLocation, pickUpLocations])

    // monitor update status - V2: uses timeline-bookings slice directly
    // updateTimelineBooking.fulfilled updates bookingsByFlight in place
    useEffect(() => {

        if (updateStatus == 'succeeded') {

            const finish = () => {
                // show success modal
                dispatch(resetForm());
                dispatch(resetUpdateBooking());
                if (props?.handleGetBookings) props?.handleGetBookings();
                props?.close();
            };

            if (props?.forCheckIn && selectedPickUp?.value && booking?.flightConfirmationDetails?.origin == dbUser?.details?.authority) {
                // setBookingLocation is the only write for a departure check-in: it sets
                // paxPositionInfo, adds the paxPositionHistory entry and the paxLocations row
                // together. Wait for it before closing - closing first hid failures and left
                // the pax shown as checked in but missing from the Locations list.
                dispatch(resetUpdateBooking());
                let object = {
                    bookingId: booking?._id,
                    location: selectedPickUp?.value,
                    locationTitle: selectedPickUp?.label,
                    activity: "Checked-in",
                    time: new Date(),
                    preboardAssistanceRequired: !isSelfAssisted,
                    ...(trimmedEmail && { passengerEmail: trimmedEmail.toLowerCase() }),
                    setBy: {
                        id: firebaseUser?.uid ?? "",
                        name: firebaseUser?.displayName ?? "",
                        from: "portal",
                        time: new Date(),
                    }
                };
                dispatch(setBookingLocation(object)).unwrap()
                    .then(finish)
                    .catch((err) => setError(`${err?.message || 'Passenger NOT checked in.'} Please press Check In again, or contact admin support at timeline-support@prmassist.com.`));
                return;
            }

            if (props?.forCheckIn && selectedArrCheckInLocation?.value && booking?.flightConfirmationDetails?.destination == dbUser?.details?.authority) {
                dispatch(arrivalPaxCheckIn({
                    bookingId: booking?._id,
                    body: {
                        type: "checkIn",
                        locationId: selectedArrCheckInLocation?.value,
                        locationTitle: selectedArrCheckInLocation?.label,
                        iata: dbUser.details.authority,
                        showInLocationsTab: showArrInLocationsTab,
                        setBy: {
                            id: firebaseUser?.uid ?? "",
                            name: firebaseUser?.displayName ?? "",
                            from: "portal",
                            time: new Date(),
                        }
                    }
                }));
            }

            finish();
        }
        if (updateStatus == 'failed') {
            // show error messaging
            setError('Error updating booking, please contact admin support at timeline-support@prmassist.com.');
        }
    }, [updateStatus, booking]);

    // set default pickup location if its checkin modal
    useEffect(() => {
        if (defaultCheckInLocation) {
            setPickUpLocation(defaultCheckInLocation);
        };
    }, [defaultCheckInLocation]);


    //#endregion


    //#region functions 

    const matchLocationByGateOrStand = (locations, gate, stand) => {

        let foundLocation = null;

        foundLocation = locations?.find(loc => (loc?.gate && loc?.gate?.trim() == gate?.trim()));


        if (Object.isObjectEmpty(foundLocation)) {

            foundLocation = locations?.find(loc => (loc?.stand && loc?.stand?.trim() == stand?.trim()));
        };

        return foundLocation;
    };

    const editBooking = () => {

        // clear error
        setError("");

        if (Object.isObjectEmpty(booking)) return;

        const assistanceChanged = !eqUnorderedStringArrays(assistance, originalAssistance);

        // Build history entry if changed
        let editAssistanceHistoric = booking.editAssistanceHistoric || [];
        if (assistanceChanged) {
            //extract author info from Firebase user
            const author = {
                id: firebaseUser?.uid || firebaseUser?.id || "unknown",
                name:
                    firebaseUser?.displayName ||
                    firebaseUser?.name ||
                    firebaseUser?.email ||
                    "Unknown",
            };

            editAssistanceHistoric = [
                ...editAssistanceHistoric,
                {
                    prev: originalAssistance,       // array of codes before change (from state)
                    next: assistance,               // array of codes after change
                    reason: editAssistanceReason?.trim() || "",
                    author,
                    editedAt: new Date().toISOString(),
                },
            ];
        }

        // Build the doc to patch
        let newBookingDoc = {
            name: `${firstName} ${secondName}`.trim(),
            assistance,
            notes: bookingNotes,
            ...(assistanceChanged ? { editAssistanceHistoric } : {}),

            preboardAssistanceRequired: !isSelfAssisted,
            selfAssisted: isSelfAssisted,
            boardingAssistNeeded: boardingAssistNeeded ? true : false,
            noShow: isNoShow,

            // add passenger boarding pickup location (only when preboard not required but boarding is required)
            boardingPickupLocation: (isSelfAssisted && boardingAssistNeeded && selectedBoardingPickup?.value) ? {
                id: selectedBoardingPickup.value,
                name: selectedBoardingPickup.label,
            } : null,


        };
        if (booking?.flightConfirmationDetails?.destination == dbUser?.details?.authority) {
            delete newBookingDoc?.preboardAssistanceRequired;
            delete newBookingDoc?.selfAssisted;
            delete newBookingDoc?.boardingAssistNeeded;
            delete newBookingDoc?.boardingPickupLocation;
        }


        const payload = {
            id: booking._id || booking.id,
            body: {
                ...newBookingDoc
            }
        }

        dispatch(updateTimelineBooking(payload));
    };

    const setPickUpLocation = (value) => {
        if (!value) {
            setSelectedPickUp(null);
        } else {
            const selectedLocation = pickUpLocations?.find((loc) => loc._id === value);
            if (selectedLocation) {
                setSelectedPickUp({
                    value: selectedLocation?._id ?? "",
                    label: selectedLocation?.title ?? ""
                });
            } else setSelectedPickUp(null);

        }
    }

    const handleChangeAssistance = (codes) => {
        const normalized = Array.isArray(codes) ? codes : getAssistanceArray(codes);
        // Only check for changes if form is initialized (originalAssistance is set)
        if (isInitialized && originalAssistance.length > 0) {
            const changed = !eqUnorderedStringArrays(normalized, originalAssistance);
            setShowAddReasonInput(changed);
            if (!changed) setEditAssistanceReason(""); // clear if not needed
        }
        setAssistance(normalized);
    };

    //#endregion

    return (
        <>
            <style>
                {`
                    .form-input::placeholder {
                    color: ${usedTheme.TEXT_COLOR} !important;
                    opacity: 1;
                    }
                `}
            </style>
            <Form>
                <div style={{ display: "grid" }}>
                    {/* Passenger Name */}
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <div style={{ width: "45%" }}>
                            <Form.Label style={{ color: Colors.PRM_LIGHT_BLUE }}>First Name</Form.Label>
                            <Form.Control
                                className="form-input"
                                type="text"
                                placeholder="Enter first name"
                                onChange={(e) => setFirstName(e.target.value)}
                                value={firstName}
                                disabled={true}
                                style={{ backgroundColor: usedTheme.BACKGROUND_COLOR, borderColor: usedTheme.BORDER_COLOR, color: usedTheme.TEXT_COLOR }}
                            />
                        </div>
                        <div style={{ width: "45%" }}>
                            <Form.Label style={{ color: Colors.PRM_LIGHT_BLUE }}>Last Name</Form.Label>
                            <Form.Control
                                className="form-input"
                                type="text"
                                placeholder="Enter last name"
                                onChange={(e) => setSecondName(e.target.value)}
                                value={secondName}
                                disabled={true}
                                style={{ backgroundColor: usedTheme.BACKGROUND_COLOR, borderColor: usedTheme.BORDER_COLOR, color: usedTheme.TEXT_COLOR }}
                            />
                        </div>
                    </div>

                    {/* Passenger Email - departure check-in only, saved by setBookingLocation */}
                    {isDepartureCheckIn && (
                        <div className="mt-3">
                            <Form.Label style={{ color: Colors.PRM_LIGHT_BLUE }}>Passenger Email (Optional)</Form.Label>
                            <Form.Control
                                className="form-input"
                                type="email"
                                placeholder="name@example.com"
                                onChange={(e) => setPassengerEmail(e.target.value)}
                                value={passengerEmail}
                                isInvalid={emailError}
                                style={{ backgroundColor: usedTheme.BACKGROUND_COLOR, borderColor: emailError ? undefined : usedTheme.BORDER_COLOR, color: usedTheme.TEXT_COLOR }}
                            />
                            <Form.Text style={{ color: emailError ? Colors.DARK_RED : usedTheme.TEXT_COLOR, opacity: emailError ? 1 : 0.7 }}>
                                {emailError
                                    ? "Enter a valid email address or leave it blank"
                                    : "Only with the passenger's agreement. Used to send them a short feedback survey after their journey."}
                            </Form.Text>
                        </div>
                    )}

                    {/* Assistance Selector */}
                    <div className="mt-4">
                        {assistanceHistoric.length > 0 && (
                            <div className="mb-2" style={{ padding: "8px", border: `1px solid ${usedTheme.BORDER_COLOR}`, borderRadius: 8 }}>
                                <Form.Label style={{ color: Colors.PRM_LIGHT_BLUE, marginBottom: 6 }}>
                                    Assistance Edit History
                                </Form.Label>

                                {assistanceHistoric.map((h, i) => (
                                    <div key={i} style={{ padding: "8px 0", borderTop: i ? `1px solid ${usedTheme.BORDER_COLOR}` : "none" }}>
                                        <div style={{ fontSize: 12, opacity: 0.8, marginBottom: 4 }}>
                                            Edited by <strong>{h?.author.name || "Unknown"}</strong> At:{formatDayTime(h?.editedAt)}
                                        </div>
                                        <div style={{ fontSize: 13 }}>
                                            <div><strong>Old:</strong> {getAssistanceString(h?.prev)}</div>
                                            <div><strong>New:</strong> {getAssistanceString(h?.next)}</div>
                                            <div style={{ marginTop: 4 }}>
                                                <strong>Reason:</strong> {h?.reason || "—"}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                        <Form.Label style={{ color: Colors.PRM_LIGHT_BLUE }}>Assistance Requirements</Form.Label>
                        <AssistanceSelector
                            setAssistance={handleChangeAssistance}
                            defaultValue={assistance}
                            value={assistance}
                        />
                        {showAddReasonInput && <Form.Control
                            className="form-input"
                            as="textarea"
                            type="text"
                            placeholder="Add reason for editing assistance (Optional)"
                            onChange={(e) => setEditAssistanceReason(e.target.value)}
                            value={editAssistanceReason || ""}
                            style={{ height: '60px', marginTop: "4px", backgroundColor: usedTheme.BACKGROUND_COLOR, borderColor: usedTheme.BORDER_COLOR, color: usedTheme.TEXT_COLOR }}
                        />}

                    </div>
                    {props?.forCheckIn && booking?.flightConfirmationDetails?.origin == dbUser?.details?.authority && (
                        <div>

                            {/* self assisted + boarding assistance on same row */}
                            <div className="mt-4" style={{ display: 'flex', gap: '32px' }}>

                                <div>
                                    <Form.Label style={{ color: Colors.PRM_LIGHT_BLUE }}>Self-Assisted Passenger</Form.Label>
                                    <p style={{ fontSize: '12px', color: usedTheme.TEXT_COLOR, opacity: 0.6 }}>
                                        Enable if the passenger can manage their own way to gate
                                    </p>
                                    <Form.Check
                                        onClick={() => setIsSelfAssisted(!isSelfAssisted)}
                                        type="switch"
                                        defaultChecked={isSelfAssisted}
                                        style={{ marginLeft: "16px" }}
                                        checked={isSelfAssisted}
                                    />
                                </div>

                                <div>
                                    <Form.Label style={{ color: Colors.PRM_LIGHT_BLUE }}>Boarding Assistance Required</Form.Label>
                                    <p style={{ fontSize: '12px', color: usedTheme.TEXT_COLOR, opacity: 0.6 }}>
                                        Enable if the passenger requires assistance during the boarding process
                                    </p>
                                    <Form.Check
                                        onClick={() => setBoardingAssistNeeded(!boardingAssistNeeded)}
                                        type="switch"
                                        defaultChecked={boardingAssistNeeded}
                                        style={{ marginLeft: "16px" }}
                                        checked={boardingAssistNeeded}
                                    />
                                    {!boardingAssistNeeded && <span style={{ color: 'red', fontSize: '10px' }}>*** Please note that if Boarding Assistance Needed is not switched on, this PAX will be marked as "No Boarding Assistance Needed".</span>}
                                </div>

                            </div>

                            {/* Boarding Pickup Location - shown when passenger IS self-assisted but boarding assistance IS required */}
                            {isSelfAssisted && boardingAssistNeeded && (
                                <div className='mt-4 d-grid'>
                                    <Form.Label style={{ color: Colors.PRM_LIGHT_BLUE }}>Pickup Location <span style={{ color: 'red' }}>* Required</span></Form.Label>
                                    <p style={{ fontSize: '12px', color: usedTheme.TEXT_COLOR, opacity: 0.8, marginBottom: '8px' }}>
                                        This will be the pickup location for the passenger for boarding assistance.
                                    </p>
                                    <Select
                                        value={selectedBoardingPickup}
                                        onChange={(selectedOption) => {
                                            setSelectedBoardingPickup(selectedOption);
                                        }}
                                        options={pickUpLocations?.map(loc => ({
                                            value: loc._id,
                                            label: loc.title
                                        }))}
                                        placeholder="Choose Pickup Location..."
                                        styles={{
                                            control: (base) => ({
                                                ...base,
                                                minWidth: "25%",
                                                borderRadius: "5px",
                                                backgroundColor: usedTheme.BACKGROUND_COLOR,
                                                borderColor: usedTheme.BORDER_COLOR,
                                                color: usedTheme.TEXT_COLOR,
                                            }),
                                            placeholder: (provided) => ({
                                                ...provided,
                                                color: usedTheme.TEXT_COLOR,
                                            }),
                                            singleValue: (provided) => ({
                                                ...provided,
                                                color: usedTheme.TEXT_COLOR,
                                            }),
                                            menu: (provided) => ({
                                                ...provided,
                                                backgroundColor: usedTheme.BACKGROUND_COLOR,
                                                borderColor: usedTheme.BORDER_COLOR,
                                                color: usedTheme.TEXT_COLOR
                                            }),
                                            option: (provided, state) => ({
                                                ...provided,
                                                backgroundColor: state.isFocused
                                                    ? usedTheme.BORDER_COLOR
                                                    : usedTheme.BACKGROUND_COLOR,
                                                color: usedTheme.TEXT_COLOR,
                                                cursor: 'pointer',
                                            }),
                                        }}
                                        isClearable
                                    />
                                </div>
                            )}

                            <div className='mt-4 d-grid'>
                                <Form.Label style={{ color: Colors.PRM_LIGHT_BLUE }}>{props?.locationSelectTitle ? props?.locationSelectTitle : "Pick Up Location (Optional)"}</Form.Label>
                                <Select
                                    value={selectedPickUp}
                                    onChange={(selectedOption) => {
                                        setSelectedPickUp(selectedOption);
                                        setPickUpLocation(selectedOption?.value);
                                    }}
                                    options={pickUpLocations?.map(loc => ({
                                        value: loc._id,
                                        label: loc.title
                                    }))}
                                    placeholder={props?.placeholder ? props?.placeholder : "Choose Pick Up Location..."}
                                    styles={{
                                        control: (base) => ({
                                            ...base,
                                            minWidth: "25%",
                                            borderRadius: "5px",
                                            backgroundColor: usedTheme.BACKGROUND_COLOR,
                                            borderColor: usedTheme.BORDER_COLOR,
                                            color: usedTheme.TEXT_COLOR,
                                        }),
                                        placeholder: (provided) => ({
                                            ...provided,
                                            color: usedTheme.TEXT_COLOR,
                                        }),
                                        singleValue: (provided) => ({
                                            ...provided,
                                            color: usedTheme.TEXT_COLOR,
                                        }),
                                        menu: (provided) => ({
                                            ...provided,
                                            backgroundColor: usedTheme.BACKGROUND_COLOR,
                                            borderColor: usedTheme.BORDER_COLOR,
                                            color: usedTheme.TEXT_COLOR
                                        }),
                                        option: (provided, state) => ({
                                            ...provided,
                                            backgroundColor: state.isFocused
                                                ? usedTheme.BORDER_COLOR
                                                : usedTheme.BACKGROUND_COLOR,
                                            color: usedTheme.TEXT_COLOR,
                                            cursor: 'pointer',
                                        }),
                                    }}
                                    isClearable
                                />
                            </div>

                        </div>
                    )
                    }

                    {/* Check is arrival select location */}

                    {props?.forCheckIn && booking?.flightConfirmationDetails?.destination == dbUser?.details?.authority && (
                        <div>
                            <div className='mt-4 d-grid'>
                                <Form.Label style={{ color: Colors.PRM_LIGHT_BLUE }}>{props?.locationSelectTitle ? props?.locationSelectTitle : "Pick Up Location (Optional)"}</Form.Label>
                                <Select
                                    value={selectedArrCheckInLocation}
                                    onChange={(selectedOption) => {
                                        setSelectedArrCheckInLocation(selectedOption);
                                    }}
                                    options={pickUpLocations?.map(loc => ({
                                        value: loc._id,
                                        label: loc.title
                                    }))}
                                    placeholder={props?.placeholder ? props?.placeholder : "Choose Pick Up Location..."}
                                    styles={{
                                        control: (base) => ({
                                            ...base,
                                            minWidth: "25%",
                                            borderRadius: "5px",
                                            backgroundColor: usedTheme.BACKGROUND_COLOR,
                                            borderColor: usedTheme.BORDER_COLOR,
                                            color: usedTheme.TEXT_COLOR,
                                        }),
                                        placeholder: (provided) => ({
                                            ...provided,
                                            color: usedTheme.TEXT_COLOR,
                                        }),
                                        singleValue: (provided) => ({
                                            ...provided,
                                            color: usedTheme.TEXT_COLOR,
                                        }),
                                        menu: (provided) => ({
                                            ...provided,
                                            backgroundColor: usedTheme.BACKGROUND_COLOR,
                                            borderColor: usedTheme.BORDER_COLOR,
                                            color: usedTheme.TEXT_COLOR
                                        }),
                                        option: (provided, state) => ({
                                            ...provided,
                                            backgroundColor: state.isFocused
                                                ? usedTheme.BORDER_COLOR
                                                : usedTheme.BACKGROUND_COLOR,
                                            color: usedTheme.TEXT_COLOR,
                                            cursor: 'pointer',
                                        }),
                                    }}
                                    isClearable
                                />
                            </div>
                            {selectedArrCheckInLocation && arrForLocationsTab && <div className="mt-4">
                                <Form.Label style={{ color: Colors.PRM_LIGHT_BLUE }}>Show in Locations Tab</Form.Label>
                                <p style={{ fontSize: '12px', color: usedTheme.TEXT_COLOR, opacity: 0.6 }}>
                                    {showArrInLocationsTab ? "This PAX will be shown in the locations tab." : "This PAX will not be shown in the locations tab."}
                                </p>
                                <Form.Check
                                    onClick={() => setShowArrInLocationsTab(!showArrInLocationsTab)}
                                    type="switch"
                                    defaultChecked={showArrInLocationsTab}
                                    style={{ marginLeft: "16px" }}
                                    checked={showArrInLocationsTab}
                                />
                            </div>}
                        </div>
                    )}

                    {/* Booking Notes */}
                    <div className="mt-3">
                        <BookingNotesSuggestions
                            setBookingNotes={setBookingNotes}
                            bookingNotes={bookingNotes}
                            booking={booking}
                        />
                    </div>

                    {/* No Show Toggle - only for departure bookings */}
                    {booking?.flightConfirmationDetails?.origin === dbUser?.details?.authority && (
                        <div className="mt-4" style={{ display: 'flex', justifyContent: 'flex-start' }}>
                            <div>
                                <Form.Label style={{ color: Colors.PRM_LIGHT_BLUE }}>No Show</Form.Label>
                                <p style={{ fontSize: '12px', color: usedTheme.TEXT_COLOR, opacity: 0.6 }}>
                                    Mark this passenger as a no-show if they did not arrive for their flight
                                </p>
                            </div>
                            <div className='mt-3'>
                                <Form.Check
                                    onClick={() => setIsNoShow(!isNoShow)}
                                    type="switch"
                                    defaultChecked={isNoShow}
                                    style={{ marginLeft: "16px" }}
                                    checked={isNoShow}
                                />
                            </div>
                        </div>
                    )}

                    {/* Location required alert for check-in */}
                    {props?.forCheckIn && (() => {
                        const isDeparture = booking?.flightConfirmationDetails?.origin == dbUser?.details?.authority;
                        const isArrival = booking?.flightConfirmationDetails?.destination == dbUser?.details?.authority;
                        const missingLocation = (isDeparture && !selectedPickUp?.value) || (isArrival && !selectedArrCheckInLocation?.value);
                        return missingLocation ? (
                            <Alert variant="warning" className="mt-4 mb-0" style={{ fontSize: '13px' }}>
                                Please select a check-in location before checking in the passenger.
                            </Alert>
                        ) : null;
                    })()}

                    {/* Save Button */}
                    <div className="mt-4" style={{ display: "flex", justifyContent: "center" }}>
                        <Button
                            variant="outline-primary"
                            onClick={editBooking}
                            style={{ width: 200 }}
                            disabled={
                                updateStatus == 'loading' || setBookingLocationStatus == 'loading'
                                || !firstName?.length || !secondName?.length || !assistance?.length
                                || emailError
                                || (isSelfAssisted && boardingAssistNeeded && !selectedBoardingPickup?.value)
                                || (props?.forCheckIn && booking?.flightConfirmationDetails?.origin == dbUser?.details?.authority && !selectedPickUp?.value)
                                || (props?.forCheckIn && booking?.flightConfirmationDetails?.destination == dbUser?.details?.authority && !selectedArrCheckInLocation?.value)
                            }
                        >
                            {updateStatus == 'loading' || setBookingLocationStatus == 'loading' ? <CustomSpinner variant="light" size={"small"} /> : props?.btnTitle ? props?.btnTitle : 'Save Booking'}
                        </Button>
                    </div>

                    {/* error message */}
                    {errorMessage && (
                        <div className="mt-3" style={{ color: Colors.RED, textAlign: "center", color: 'red' }}>
                            {errorMessage}
                        </div>
                    )}

                </div>
            </Form>

        </>
    )
}

export default EditTimelineBookingFormV2;
